import * as React from 'react';
import MediaQuery from '../../../../../MediaQuery/MediaQuery';
import ModalConfirm from '../../../../../ModalConfirm';
import { FooterMobile, MobileDialog } from '@shared/ui';
import { isGdsServiceGuard } from '@widgets/upsale/lib/guards';
import { useSelector } from 'react-redux';
import { getGdsServicesById, getOrderId, getTravellers } from '../../../../../Checkout/store/order/selectors';
import { isUpsaleServiceTypeWasShown, setUpsaleServiceTypeWasShown } from '@widgets/upsale/lib/isServiceTypeWasShown';
import { useCallback, useEffect, useMemo } from 'react';
import { useToggleable } from '../../../../../hooks';
import { UpsaleService } from '@widgets/upsale';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../../../theme';
import { useConfig } from '../../../../../context';
const Upsale = ({ only, onSave }) => {
    const { t } = useTranslation('Upsale');
    const orderId = useSelector(getOrderId);
    const servicesById = useSelector(getGdsServicesById);
    const travellers = useSelector(getTravellers);
    const { isOpen, setOpen } = useToggleable(false);
    const { Upsale: { Upsale: theme } } = useTheme('Widgets');
    const { global: { savePreselectedWithoutUserAction } } = useConfig();
    const filteredServices = useMemo(() => {
        var _a, _b, _c;
        const servicesMap = new Map();
        for (const traveller of travellers) {
            for (const service of (_c = (_b = (_a = traveller.preselectedServices) === null || _a === void 0 ? void 0 : _a.gdsServices) === null || _b === void 0 ? void 0 : _b.services) !== null && _c !== void 0 ? _c : []) {
                const previousService = servicesMap.get(service.serviceId);
                if (!servicesById.hasOwnProperty(service.serviceId)) {
                    console.warn(`traveller ${traveller.id} preselected service without valid service_id ${service.serviceId}`);
                    continue;
                }
                const currentAmount = servicesById[service.serviceId].price.amount * service.segmentIds.length;
                const currency = servicesById[service.serviceId].price.currency;
                if (previousService && isGdsServiceGuard(previousService)) {
                    const previousAmount = servicesById[previousService.serviceId].price.amount * previousService.segmentIds.length;
                    servicesMap.set(service.serviceId, Object.assign(Object.assign({}, previousService), { price: { amount: previousAmount + currentAmount, currency: currency } }));
                }
                else {
                    servicesMap.set(service.serviceId, Object.assign(Object.assign({}, service), { price: servicesById[service.serviceId].price }));
                }
            }
        }
        return [...servicesMap.values()].filter(service => {
            if (isGdsServiceGuard(service) && only.includes(servicesById[service.serviceId].type)) {
                const additionalService = servicesById[service.serviceId];
                return !isUpsaleServiceTypeWasShown(orderId, additionalService.type);
            }
            return false;
        });
    }, [travellers, servicesById, orderId, only]);
    const markAllTypesAsShown = () => only.forEach(type => setUpsaleServiceTypeWasShown(orderId, type));
    const handleClose = useCallback(() => {
        markAllTypesAsShown();
        setOpen(false);
    }, [only, filteredServices, servicesById]);
    const handleConfirm = useCallback(() => {
        markAllTypesAsShown();
        const servicePayload = [];
        filteredServices.filter(isGdsServiceGuard).forEach(service => {
            var _a, _b;
            for (const traveller of travellers) {
                for (const preselectedService of (_b = (_a = traveller.preselectedServices) === null || _a === void 0 ? void 0 : _a.gdsServices) === null || _b === void 0 ? void 0 : _b.services) {
                    if (preselectedService.serviceId === service.serviceId) {
                        servicePayload.push({
                            passengerId: traveller.id,
                            segmentId: preselectedService.segmentIds[0],
                            serviceId: preselectedService.serviceId,
                            count: service.count,
                            service: {
                                id: preselectedService.serviceId,
                                type: servicesById[preselectedService.serviceId].type,
                                confirmedCount: preselectedService.confirmedCount,
                                price: servicesById[preselectedService.serviceId].price,
                                name: servicesById[preselectedService.serviceId].name
                            }
                        });
                    }
                }
            }
        });
        setOpen(false);
        onSave(servicePayload);
    }, [filteredServices, travellers, servicesById, onSave, orderId, only]);
    const gdsServicesList = useMemo(() => {
        return filteredServices
            .filter(isGdsServiceGuard)
            .map(service => (React.createElement(UpsaleService, { key: service.serviceId, service: servicesById[service === null || service === void 0 ? void 0 : service.serviceId], preselectedService: service, className: theme.upsaleItem })));
    }, [filteredServices]);
    useEffect(() => {
        if (filteredServices.length &&
            only.every(upsaleType => savePreselectedWithoutUserAction.types.includes(upsaleType))) {
            handleConfirm();
            setOpen(false);
            return;
        }
        if (filteredServices.length) {
            setOpen(true);
        }
    }, [filteredServices, handleConfirm, only, savePreselectedWithoutUserAction, setOpen]);
    if (!filteredServices.length) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(MediaQuery, { minWidth: 'tablet' },
            React.createElement(ModalConfirm, { classes: {
                    title: theme.title,
                    content: theme.content
                }, title: t('Would you like to add one of these services to your order?'), onConfirm: handleConfirm, onCancel: handleClose, onClose: handleClose, confirmBtn: t('Confirm'), cancelBtn: t('Cancel'), isOpen: isOpen, size: filteredServices.length === 1 ? 'small' : 'large' }, gdsServicesList)),
        React.createElement(MediaQuery, { maxWidth: 'tablet' },
            filteredServices.length > 1 && (React.createElement(MobileDialog, { title: t('Would you like to add one of these services to your order?'), isOpen: isOpen, onClose: handleClose, classes: { content: theme.content }, footer: React.createElement(FooterMobile, { onContinue: handleConfirm, onBack: handleClose, continueButtonText: t('Confirm'), backButtonText: t('Cancel') }) }, gdsServicesList)),
            filteredServices.length === 1 && (React.createElement(ModalConfirm, { size: 'xlarge', onConfirm: handleConfirm, onCancel: handleClose, isOpen: isOpen, confirmBtn: t('Confirm'), cancelBtn: t('Cancel') }, gdsServicesList)))));
};
export default Upsale;
